import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Footer from "../shared/Footer";
import Navbar from "../shared/Navbar";
import Sidebar from "../shared/Sidebar";
import Swal from "sweetalert2";
import config from "../../config";

function SpecialExamSchedule() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("secondaryAdminToken");

  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [timeSlots, setTimeSlots] = useState();
  const [isFinalSaved, setIsFinalSaved] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFinalSave, setShowFinalSave] = useState(false);

  const filteredSubjects = subjects.filter(
    (sub) =>
      (sub.subjectName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.subjectCode || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.subjectType || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(sub.eligibleStudentCount || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.examDate || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.examTime || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (!token) {
      navigate("/secondary-admin");
    } else {
      fetchPageLoadData();
    }
  }, [token]);

  const fetchPageLoadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL2}/CSExamFetchExaminationSchedule_CL10`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (response.ok) {
        // Handle variations in structure (direct array, array under data, or examinationSchedule_list)
        let loadedSubjects = [];
        // debugger;
        if (data.data) {
          if (Array.isArray(data.data)) {
            loadedSubjects = data.data;
          } else if (Array.isArray(data.data.examinationScheduleDetails)) {
            loadedSubjects = data.data.examinationScheduleDetails;
          } else if (Array.isArray(data.data.examinationSchedule_list)) {
            loadedSubjects = data.data.examinationSchedule_list;
          }
        } else if (Array.isArray(data)) {
          loadedSubjects = data;
        }

        // Parse time slots / shifts from API
        let loadedTimeSlots = [];
        const timeStatusSrc = data.data?.examTimeStatus || data.examTimeStatus;
        if (timeStatusSrc && Array.isArray(timeStatusSrc)) {
          loadedTimeSlots = timeStatusSrc.map((slot) => (slot.exaM_TIME || slot.time || slot.examTime || "").trim());
        } else {
          const slotsSrc = data.data?.timeSlots || data.timeSlots;
          if (slotsSrc && Array.isArray(slotsSrc)) {
            loadedTimeSlots = slotsSrc.map((slot) => (slot || "").trim());
          }
        }

        // Collect any non-empty time slots present in the subject list alongside examTimeStatus
        const subjectTimes = loadedSubjects.map((sub) => (sub.time || sub.examTime || sub.exaM_TIME || "").trim()).filter(Boolean);
        const apiTimes = Array.from(new Set([...loadedTimeSlots, ...subjectTimes].filter(Boolean)));
        const activeTimeSlots = apiTimes.length > 0 ? apiTimes : [""];
        setTimeSlots(activeTimeSlots);

        let hasInvalidTime = false;
        const formattedSubjects = loadedSubjects.map((sub) => {
          const rawTime = (sub.time || sub.examTime || sub.exaM_TIME || "").trim();
          const isValidTime = !rawTime || activeTimeSlots.includes(rawTime);
          if (rawTime && !isValidTime) {
            hasInvalidTime = true;
          }
          const allowedTime = isValidTime ? rawTime : "";
          return {
            subjectCode: sub.subjecT_ID || sub.subjectCode || sub.subjecT_CODE || "",
            subjectName: sub.subjecT_NAME || sub.subjectName || "",
            subjectType: sub.subjecT_TYPE || sub.subjectType || sub.type || sub.subject_type || sub.subjecT_CAT || sub.subjectCategory || "COMPULSORY",
            eligibleStudentCount: sub.eligiblE_CANDIDATE !== undefined ? sub.eligiblE_CANDIDATE : (sub.eligibleStudentCount || sub.eligiblE_STUDENT_COUNT || 0),
            examDate: sub.date || sub.examDate || sub.exaM_DATE || "",
            examTime: allowedTime,
          };
        });
        setSubjects(formattedSubjects);

        const nonZeroSubjects = formattedSubjects.filter(sub => Number(sub.eligibleStudentCount || 0) > 0);
        const allHaveDateTime = nonZeroSubjects.length > 0 && nonZeroSubjects.every(sub => sub.examDate && sub.examTime);
        setShowFinalSave(allHaveDateTime);

        if (hasInvalidTime) {
          Swal.fire(
            "Warning",
            `Some subjects have exam times other than those allowed by the API (${activeTimeSlots.join(", ")}). These invalid times have been cleared and will not be saved.`,
            "warning"
          );
        }

        let isFinal = false;
        if (data?.data?.typeStatus?.savE_TYPE_STATUS === "YES" || data?.typeStatus?.savE_TYPE_STATUS === "YES") {
          isFinal = true;
        } else if (data?.data) {
          if (
            data.data.isFinalSubmit === true ||
            data.data.isFinalSave === true ||
            data.data.isFinalSaved === true ||
            data.data.status === "FINAL" ||
            data.data.saveType === "FINAL" ||
            data.data.finaL_SAVE === "YES" ||
            data.data.finaL_SAVE_STATUS === "YES" ||
            data.data.finalSaveStatus === "YES" ||
            data.data.finaL_SUBMIT === "YES" ||
            data.data.savE_TYPE_STATUS === "YES" ||
            String(data.data.isFinalSave).toUpperCase() === "YES" ||
            String(data.data.isFinalSaved).toUpperCase() === "YES" ||
            String(data.data.isFinalSubmit).toUpperCase() === "YES"
          ) {
            isFinal = true;
          }
        }
        if (!isFinal && data) {
          if (
            data.finaL_SAVE === "YES" ||
            data.finaL_SAVE_STATUS === "YES" ||
            data.finalSaveStatus === "YES" ||
            data.finaL_SUBMIT === "YES" ||
            data.savE_TYPE_STATUS === "YES" ||
            String(data.isFinalSave).toUpperCase() === "YES" ||
            String(data.isFinalSaved).toUpperCase() === "YES" ||
            String(data.isFinalSubmit).toUpperCase() === "YES" ||
            data.status === "FINAL" ||
            data.saveType === "FINAL" ||
            data.isFinalSubmit === true ||
            data.isFinalSave === true ||
            data.isFinalSaved === true
          ) {
            isFinal = true;
          }
        }
        setIsFinalSaved(isFinal);
      } else {
        Swal.fire("Error", data.message || "Failed to load special exam schedule data.", "error");
      }
    } catch (error) {
      console.error("Error loading special exam schedule data:", error);
      Swal.fire("Error", "Failed to load special exam schedule data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (index, dateString) => {
    const updated = [...subjects];
    updated[index].examDate = dateString;
    setSubjects(updated);
  };

  const handleTimeChange = (index, timeString) => {
    const updated = [...subjects];
    updated[index].examTime = timeString;
    setSubjects(updated);
  };

  const handleSave = async (saveType) => {
    // Check row-level dependency: if either date or time is entered, both must be provided
    const invalidRow = subjects.find(
      (sub) => (sub.examDate && !sub.examTime) || (!sub.examDate && sub.examTime)
    );
    if (invalidRow) {
      Swal.fire(
        "Incomplete Row Details",
        `For subject "${invalidRow.subjectName}", please select both Exam Date and Time (Shift).`,
        "warning"
      );
      return;
    }

    // If Final Save, check that all date and time slots are filled
    // if (saveType === "FINAL") {
    //   const incomplete = subjects.some((sub) => !sub.examDate || !sub.examTime);
    //   if (incomplete) {
    //     Swal.fire(
    //       "Required Fields Missing",
    //       "Please select Exam Date and Time for all subjects before Final Save.",
    //       "warning"
    //     );
    //     return;
    //   }
    // }

    // Confirmation Prompts for Draft & Final Save
    const confirmTitle = saveType === "FINAL" ? "Final Save?" : "Draft Save?";
    const confirmText =
      saveType === "FINAL"
        ? "Do you really want to make this a final save? Once finalized, you cannot make any further edits."
        : "Do you really want to save this schedule as draft?";
    const confirmButtonText = saveType === "FINAL" ? "Yes, Final Save!" : "Yes, Save Draft!";

    const confirmResult = await Swal.fire({
      title: confirmTitle,
      text: confirmText,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: confirmButtonText,
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      let url = "";
      let options = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (saveType === "DRAFT") {
        url = `${config.API_BASE_URL2}/CSExamExaminationScheduleInsertDraft_CL10`;
        options.method = "POST";
        options.body = JSON.stringify({
          DraftInsertSubjectList: subjects.map((sub) => {
            const rawTime = (sub.examTime || "").trim();
            const allowedTime = (timeSlots && timeSlots.includes(rawTime)) ? rawTime : null;
            return {
              suB_CODE: sub.subjectCode,
              suB_NAME: sub.subjectName,
              date: sub.examDate || null,
              time: allowedTime,
            };
          }),
        });
      } else {
        url = `${config.API_BASE_URL2}/CSExamExaminationScheduleFinalSave_CL10`;
        options.method = "GET";
      }

      const response = await fetch(url, options);

      const data = await response.json();
      if (response.ok) {
        Swal.fire(
          "Success",
          `Special Exam Schedule saved as ${saveType.toLowerCase()} successfully!`,
          "success"
        );
        if (saveType === "FINAL") {
          setIsFinalSaved(true);
        }
        await fetchPageLoadData(); // reload fresh data
      } else {
        Swal.fire(
          "Error",
          data.message || "Failed to save exam schedule.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error saving special exam schedule:", error);
      Swal.fire(
        "Error",
        "Failed to save special exam schedule.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: "Reset Schedule?",
      text: "Are you sure you want to reset all date and time inputs to their last saved state?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reset it!"
    }).then((result) => {
      if (result.isConfirmed) {
        fetchPageLoadData();
      }
    });
  };

  return (
    <>
      {loading && (
        <div className="custom-loader-container">
          <div className="custom-loader"></div>
        </div>
      )}
      <div className="container-scroller">
        <Navbar />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="row">
                <div className="col-md-12 mb-2">
                  <section className="content-header">
                    <h1>
                      <i className="fa fa-calendar" />
                      &nbsp;Special Exam Schedule
                    </h1>
                    <ol className="breadcrumb">
                      <li>
                        <a
                          onClick={() => navigate("/secondary-admin-dashboard")}
                          style={{ cursor: "pointer" }}
                        >
                          <i
                            className="fa fa-tachometer"
                            style={{ marginRight: "5px" }}
                          ></i>{" "}
                          Home
                        </a>
                      </li>
                      <li className="active">Special Exam Schedule</li>
                    </ol>
                  </section>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <div className="card">
                    <div className="card-header bg-primary">
                      <h5 className="mb-0 p-2 text-white">Manage Special Exam Schedule</h5>
                    </div>
                    <div className="card-body">
                      {/* Search Option */}
                      <div className="d-flex justify-content-end mb-3">
                        <div style={{ width: "200px" }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="table-responsive" style={{ maxHeight: "450px" }}>
                        <table className="table table-bordered table-striped table-sm">
                          <thead>
                            <tr className="bg-light">
                              {/* <th style={{ width: "50px" }}>#</th> */}
                              <th style={{ width: "150px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Subject Type</th>
                              <th style={{ width: "150px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Subject Code</th>
                              <th style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Subject Name</th>
                              <th style={{ width: "200px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Eligible Student Counts</th>
                              <th style={{ width: "220px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Select Date</th>
                              <th style={{ width: "200px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Time (Shift)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredSubjects.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center py-3 text-muted">
                                  {subjects.length === 0 ? "No subjects found" : "No matching subjects found"}
                                </td>
                              </tr>
                            ) : (
                              filteredSubjects.map((subject) => {
                                const originalIndex = subjects.findIndex(
                                  (sub) => sub.subjectCode === subject.subjectCode && sub.subjectName === subject.subjectName
                                );
                                const targetIndex = originalIndex !== -1 ? originalIndex : 0;
                                return (
                                  <tr key={subject.subjectCode || targetIndex}>
                                    {/* <td>{targetIndex + 1}</td> */}
                                    <td>
                                      {/* <span className="badge bg-dark text-white" style={{ textTransform: "uppercase" }}> */}
                                      {subject.subjectType}
                                      {/* </span> */}
                                    </td>
                                    <td><strong>{subject.subjectCode}</strong></td>
                                    <td>{subject.subjectName}</td>
                                    <td style={{ textAlign: "center" }}>
                                      {/* <span className="badge bg-info text-white"> */}
                                      <strong>{subject.eligibleStudentCount}</strong>
                                      {/* </span> */}
                                    </td>
                                    <td>
                                      <DatePicker
                                        selected={
                                          subject.examDate
                                            ? (subject.examDate.includes("-")
                                              ? new Date(subject.examDate.split("-").reverse().join("-"))
                                              : new Date(subject.examDate))
                                            : null
                                        }
                                        onChange={(date) => {
                                          if (!date) {
                                            handleDateChange(targetIndex, "");
                                            return;
                                          }
                                          const day = String(date.getDate()).padStart(2, "0");
                                          const month = String(date.getMonth() + 1).padStart(2, "0");
                                          const year = date.getFullYear();
                                          handleDateChange(targetIndex, `${day}-${month}-${year}`);
                                        }}
                                        minDate={new Date()}
                                        dateFormat="dd-MM-yyyy"
                                        className="form-control form-control-xs"
                                        placeholderText="dd-mm-yyyy"
                                        onKeyDown={(e) => e.preventDefault()}
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={10}
                                        isClearable
                                        disabled={loading || isFinalSaved}
                                      />
                                    </td>
                                    <td>
                                      <select
                                        className="form-control form-control-xs"
                                        value={subject.examTime}
                                        onChange={(e) => handleTimeChange(targetIndex, e.target.value)}
                                        style={{ color: subject.examTime ? "#000" : "#c9c8c8" }}
                                        disabled={loading || isFinalSaved}
                                      >
                                        <option value="" style={{ color: "#c9c8c8" }}>-- Select Time --</option>
                                        {Array.from(new Set([...timeSlots, subject.examTime])).filter(Boolean).map((slot) => (
                                          <option key={slot} value={slot} style={{ color: "#000" }}>
                                            {slot}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="card-footer bg-white d-flex justify-content-between align-items-center py-3">
                      <div>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={handleReset}
                          disabled={loading || isFinalSaved}
                        >
                          <i className="fa fa-refresh"></i>&nbsp;Reset
                        </button>
                      </div>
                      <div className="text-right" style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="btn btn-secondary me-2"
                          style={{ marginRight: "10px" }}
                          onClick={() => handleSave("DRAFT")}
                          disabled={loading || isFinalSaved}
                        >
                          <i className="fa fa-save"></i>&nbsp;Draft Save
                        </button>
                        {showFinalSave && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleSave("FINAL")}
                            disabled={loading || isFinalSaved}
                          >
                            <i className="fa fa-check"></i>&nbsp;Final Save
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default SpecialExamSchedule;
